import { openDB, type DBSchema, type IDBPDatabase } from 'idb';

interface FBCFavoritesDB extends DBSchema {
  favorites: {
    key: string; // slug
    value: {
      slug: string;
      addedAt: string;
      lastPrecacheVersion?: number;
    };
  };
  pending: {
    key: number;
    value: {
      id?: number;
      slug: string;
      action: 'favorite' | 'unfavorite';
      at: string;
    };
    indexes: { 'by-slug': string };
  };
  meta: {
    key: string;
    value: { id: string; value: string };
  };
}

let dbPromise: Promise<IDBPDatabase<FBCFavoritesDB>> | null = null;

function getDb() {
  if (!dbPromise) {
    dbPromise = openDB<FBCFavoritesDB>('fbc-knots', 1, {
      upgrade(db) {
        db.createObjectStore('favorites', { keyPath: 'slug' });
        const pending = db.createObjectStore('pending', { keyPath: 'id', autoIncrement: true });
        pending.createIndex('by-slug', 'slug');
        db.createObjectStore('meta', { keyPath: 'id' });
      },
    });
  }
  return dbPromise;
}

function randomClientId(): string {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export async function getClientId(): Promise<string> {
  const db = await getDb();
  const existing = await db.get('meta', 'clientId');
  if (existing?.value) return existing.value;
  const id = randomClientId();
  await db.put('meta', { id: 'clientId', value: id });
  return id;
}

export async function listFavorites(): Promise<string[]> {
  const db = await getDb();
  const all = await db.getAll('favorites');
  return all.map((f) => f.slug);
}

export async function isFavorited(slug: string): Promise<boolean> {
  const db = await getDb();
  return !!(await db.get('favorites', slug));
}

export async function setFavoriteLocal(slug: string, favorited: boolean, version?: number): Promise<void> {
  const db = await getDb();
  if (favorited) {
    await db.put('favorites', {
      slug,
      addedAt: new Date().toISOString(),
      lastPrecacheVersion: version,
    });
  } else {
    await db.delete('favorites', slug);
  }
}

export async function enqueuePending(slug: string, action: 'favorite' | 'unfavorite'): Promise<void> {
  const db = await getDb();
  await db.add('pending', { slug, action, at: new Date().toISOString() });
}

export async function drainPending(
  handler: (entry: { slug: string; action: 'favorite' | 'unfavorite' }) => Promise<boolean>,
): Promise<void> {
  const db = await getDb();
  const all = await db.getAll('pending');
  for (const entry of all) {
    try {
      const ok = await handler({ slug: entry.slug, action: entry.action });
      if (ok && entry.id != null) await db.delete('pending', entry.id);
    } catch {
      // leave in queue for next flush
    }
  }
}

export async function persistStorageIfPossible(): Promise<boolean> {
  if (!navigator.storage?.persist) return false;
  if (await navigator.storage.persisted?.()) return true;
  try {
    return await navigator.storage.persist();
  } catch {
    return false;
  }
}

export async function sendPrecacheMessage(urls: string[]): Promise<void> {
  if (urls.length === 0) return;
  if (!navigator.serviceWorker?.controller) return;
  navigator.serviceWorker.controller.postMessage({ type: 'PRECACHE_KNOT', urls });
}

export async function sendUncacheMessage(urls: string[]): Promise<void> {
  if (urls.length === 0) return;
  if (!navigator.serviceWorker?.controller) return;
  navigator.serviceWorker.controller.postMessage({ type: 'UNCACHE_KNOT', urls });
}
